import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Download,
    Sparkles,
    ShieldCheck,
    ArrowLeft,
    Loader2,
    Headphones,
    Check,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 6;
const AUDIO_PRODUCT_ID = "sleep_reset_audio";

function AudioUpsell({ buyerEmail }) {
    const [price, setPrice] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;
        axios
            .get(`${API}/product/${AUDIO_PRODUCT_ID}`)
            .then((res) => {
                if (!cancelled) setPrice(res.data);
            })
            .catch(() => {
                /* silent */
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const formatPrice = (amount, currency) => {
        if (!amount || !currency) return "$9";
        try {
            return new Intl.NumberFormat("en-US", {
                style: "currency",
                currency,
                maximumFractionDigits: currency === "NGN" ? 0 : 2,
            }).format(amount);
        } catch {
            return `${amount} ${currency}`;
        }
    };

    const handleAdd = async () => {
        if (!buyerEmail) {
            setError("We need your email to send the audio companion.");
            return;
        }
        setLoading(true);
        setError("");
        try {
            const res = await axios.post(`${API}/checkout/session`, {
                product_id: AUDIO_PRODUCT_ID,
                email: buyerEmail,
                origin_url: window.location.origin,
            });
            if (res.data?.url) {
                window.location.href = res.data.url;
            } else {
                setError("Couldn't open checkout. Please try again.");
                setLoading(false);
            }
        } catch (e) {
            setError(e.response?.data?.detail || "Checkout failed. Try again in a moment.");
            setLoading(false);
        }
    };

    const priceLabel = price ? formatPrice(price.amount, price.currency) : "$9";

    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="mt-16 text-left card-base p-8 md:p-10 border-clay/20 bg-ink-800/60"
            data-testid="audio-upsell-card"
        >
            <div className="flex items-start gap-4 mb-5">
                <div className="shrink-0 w-12 h-12 rounded-full bg-clay/15 border border-clay/30 flex items-center justify-center">
                    <Headphones size={20} className="text-clay" />
                </div>
                <div>
                    <p className="label-eyebrow mb-1 text-clay">One-time offer for buyers</p>
                    <h2 className="font-serif text-2xl md:text-3xl text-cream mb-2">
                        Add the <em>Audio Companion</em>.
                    </h2>
                    <p className="text-cream-muted text-sm md:text-base leading-relaxed">
                        A calmly-narrated version of the full guide — Days 1 through 7,
                        intro, and the 4-7-8 breath, all read aloud in a slow, low voice.
                        Built for tired eyes: listen on a walk, on the commute, or with the
                        lights already off.
                    </p>
                </div>
            </div>

            <ul className="space-y-2 mb-6 ml-16 text-sm text-cream-muted">
                <li className="flex items-center gap-2">
                    <Check size={14} className="text-sage" />
                    Roughly 90 minutes of audio (MP3, downloadable)
                </li>
                <li className="flex items-center gap-2">
                    <Check size={14} className="text-sage" />
                    Each day as a separate track — easy to revisit
                </li>
                <li className="flex items-center gap-2">
                    <Check size={14} className="text-sage" />
                    Delivered to <span className="text-cream">{buyerEmail || "your inbox"}</span> within 48 hours
                </li>
            </ul>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 ml-16">
                <p className="text-cream font-serif text-2xl">
                    {priceLabel}
                    <span className="text-sm text-cream-muted/70 ml-2 line-through">
                        {price?.currency === "NGN" ? "₦24,000" : "$19"}
                    </span>
                </p>
                <button
                    onClick={handleAdd}
                    disabled={loading}
                    className="btn-primary"
                    data-testid="audio-upsell-button"
                >
                    {loading ? (
                        <>
                            <Loader2 size={16} className="animate-spin" /> Opening checkout…
                        </>
                    ) : (
                        <>
                            Add the audio companion <ArrowLeft size={16} className="rotate-180" />
                        </>
                    )}
                </button>
            </div>
            {error && (
                <p className="text-sm text-rose-400 mt-3 ml-16" data-testid="audio-upsell-error">
                    {error}
                </p>
            )}
            <p className="text-xs text-cream-muted/60 mt-5 ml-16">
                No subscription. Same 30-day refund. Cancel by replying to the receipt email.
            </p>
        </motion.div>
    );
}

export default function Success() {
    const [params] = useSearchParams();
    // Paystack appends ?reference=xxx&trxref=xxx to the callback URL.
    // Fall back to legacy session_id for any old links.
    const reference =
        params.get("reference") || params.get("trxref") || params.get("session_id");

    const [status, setStatus] = useState("loading"); // loading | paid | pending | expired | error
    const [token, setToken] = useState(null);
    const [polls, setPolls] = useState(0);
    const [productId, setProductId] = useState(null);
    const [buyerEmail, setBuyerEmail] = useState(null);

    const checkStatus = useCallback(
        async (attempt) => {
            if (!reference) {
                setStatus("error");
                return;
            }
            try {
                const res = await axios.get(`${API}/checkout/status/${reference}`);
                const data = res.data;
                if (data.product_id) setProductId(data.product_id);
                if (data.email) setBuyerEmail(data.email);
                if (data.payment_status === "paid") {
                    setToken(data.download_token);
                    setStatus("paid");
                    return;
                }
                if (data.status === "abandoned" || data.status === "failed") {
                    setStatus("expired");
                    return;
                }
                if (attempt + 1 >= MAX_POLLS) {
                    setStatus("pending");
                    return;
                }
                setPolls(attempt + 1);
                setTimeout(() => checkStatus(attempt + 1), POLL_INTERVAL_MS);
            } catch (e) {
                console.error(e);
                setStatus("error");
            }
        },
        [reference]
    );

    useEffect(() => {
        checkStatus(0);
    }, [checkStatus]);

    const isAudioPurchase = productId === AUDIO_PRODUCT_ID;
    const downloadUrl =
        token && reference && !isAudioPurchase
            ? `${API}/download/${reference}/${token}`
            : null;

    return (
        <div className="min-h-screen dawn-gradient grain flex items-center justify-center px-6 py-16">
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                className="max-w-2xl w-full text-center"
                data-testid="success-page"
            >
                {status === "loading" && (
                    <>
                        <Loader2 size={32} className="text-clay animate-spin mx-auto mb-6" />
                        <h1 className="heading-display text-4xl md:text-5xl mb-4">
                            Confirming your payment…
                        </h1>
                        <p className="text-cream-muted">
                            One moment while Paystack hands the receipt over. Attempt {polls + 1}/{MAX_POLLS}.
                        </p>
                    </>
                )}

                {status === "paid" && !isAudioPurchase && (
                    <>
                        <div
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sage/15 border border-sage/30 text-sage text-sm mb-8"
                            data-testid="success-badge"
                        >
                            <Sparkles size={14} /> Payment confirmed
                        </div>
                        <p className="label-eyebrow mb-4">Your rest begins now</p>
                        <h1
                            className="heading-display text-5xl md:text-6xl mb-6"
                            data-testid="success-headline"
                        >
                            Welcome to <span className="italic text-clay">night one.</span>
                        </h1>
                        <p className="text-lg text-cream-muted mb-12 font-light max-w-lg mx-auto leading-relaxed">
                            Your copy of <em>The 7-Day Sleep Reset</em> is ready. Download it now and read
                            the introduction before bed tonight. Tomorrow you start Day&nbsp;1.
                        </p>
                        <a
                            href={downloadUrl}
                            download="The-7-Day-Sleep-Reset.pdf"
                            className="btn-primary inline-flex"
                            data-testid="download-pdf-button"
                        >
                            <Download size={18} />
                            Download the guide (PDF)
                        </a>
                        <div className="mt-10 grid sm:grid-cols-3 gap-4 text-sm text-cream-muted">
                            <div className="card-base p-5">
                                <p className="font-serif text-clay text-xl mb-1">01</p>
                                <p>Read the intro tonight</p>
                            </div>
                            <div className="card-base p-5">
                                <p className="font-serif text-clay text-xl mb-1">02</p>
                                <p>Start Day 1 tomorrow morning</p>
                            </div>
                            <div className="card-base p-5">
                                <p className="font-serif text-clay text-xl mb-1">03</p>
                                <p>Use the tracker on page 11</p>
                            </div>
                        </div>

                        <AudioUpsell buyerEmail={buyerEmail} />

                        <p className="mt-12 text-xs text-cream-muted/70 flex items-center justify-center gap-2">
                            <ShieldCheck size={14} /> 30-day refund guarantee · email hello@restfulpress.co
                        </p>
                    </>
                )}

                {status === "paid" && isAudioPurchase && (
                    <>
                        <div
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sage/15 border border-sage/30 text-sage text-sm mb-8"
                            data-testid="audio-success-badge"
                        >
                            <Headphones size={14} /> Audio companion confirmed
                        </div>
                        <h1
                            className="heading-display text-5xl md:text-6xl mb-6"
                            data-testid="audio-success-headline"
                        >
                            Press play <span className="italic text-clay">soon.</span>
                        </h1>
                        <p className="text-lg text-cream-muted mb-10 font-light max-w-lg mx-auto leading-relaxed">
                            Thank you. The narrated edition will arrive in your inbox at{" "}
                            <span className="text-cream">{buyerEmail}</span> within 48 hours, with each
                            day as a separate track.
                        </p>
                        <Link to="/" className="btn-secondary" data-testid="back-to-home-audio">
                            <ArrowLeft size={18} /> Back to the guide
                        </Link>
                        <p className="mt-12 text-xs text-cream-muted/70 flex items-center justify-center gap-2">
                            <ShieldCheck size={14} /> 30-day refund · reply to the receipt to cancel
                        </p>
                    </>
                )}

                {status === "pending" && (
                    <>
                        <h1 className="heading-display text-4xl md:text-5xl mb-4">
                            Almost there…
                        </h1>
                        <p className="text-cream-muted mb-8">
                            Paystack is still confirming the payment. This is normal — refresh in a few seconds
                            and your download will appear.
                        </p>
                        <button
                            onClick={() => {
                                setStatus("loading");
                                setPolls(0);
                                checkStatus(0);
                            }}
                            className="btn-primary"
                            data-testid="recheck-button"
                        >
                            Check again
                        </button>
                    </>
                )}

                {status === "expired" && (
                    <>
                        <h1 className="heading-display text-4xl md:text-5xl mb-4">Session expired</h1>
                        <p className="text-cream-muted mb-8">
                            That checkout session is no longer active. No charge was made. Head back and try again.
                        </p>
                        <Link to="/" className="btn-primary" data-testid="back-to-home">
                            <ArrowLeft size={18} /> Back to the guide
                        </Link>
                    </>
                )}

                {status === "error" && (
                    <>
                        <h1 className="heading-display text-4xl md:text-5xl mb-4">
                            Something went sideways
                        </h1>
                        <p className="text-cream-muted mb-8">
                            We couldn't confirm the payment automatically. If you were charged, email us with
                            this id and we'll send the PDF immediately:
                        </p>
                        {reference && (
                            <code className="block px-4 py-3 rounded-lg bg-ink-800 border border-cream-soft/10 text-cream-muted text-xs break-all mb-8">
                                {reference}
                            </code>
                        )}
                        <Link to="/" className="btn-secondary" data-testid="back-to-home-error">
                            <ArrowLeft size={18} /> Back to home
                        </Link>
                    </>
                )}
            </motion.div>
        </div>
    );
}
